import { Url } from "next/dist/shared/lib/router/router";
import { useRouter } from "next/router";
import { useMemo } from "react";

import { paths } from "~/paths";

export interface AppQuery {
	settingsTab?:
		| "user"
		| "company"
		| "team"
		| "customs"
		| "user-permissions"
		| "addons"
		| "reports";
	reportsTab?: "summary" | "order";
	financesTab?:
		| "monthly-statement"
		| "receipt"
		| "invoice"
		| "payment-verification";
	accountsTab?: "accounts" | "transactions";
	from?: string;
	to?: string;
	search?: string;
	page?: number;
	limit?: number;
	action?: "create" | "update" | "delete" | "monthly-statement" | "settings";
	dataType?:
		| "product_category"
		| "product_option"
		| "product_option_group"
		| "file";
	account?: string;
	userId?: string;
	agentQuery?: string;
	agentId?: string;
	counterId?: string;
	clientQuery?: string;
	clientId?: string;
	fixDiscountId?: string;
	additionalFeeId?: string;
	financeReportId?: string;
	productId?: string;
	productQueryKeyword?: string;
	productCategoryId?: string;
	productOptionId?: string;
	productOptionGroupId?: string;
	fileId?: string;
	processId?: string;
	openOrderSheet?: boolean;
}

export const useAppRouter = () => {
	const router = useRouter();
	const appQuery = useMemo<AppQuery>(() => router.query, [router.query]);

	const goToCreateNewUser = () => {
		return router.push({
			pathname: paths.settings,
			query: {
				...router.query,
				action: "create",
			},
		});
	};

	const goToUpdateUser = (id: string) => {
		return router.push({
			pathname: paths.settings,
			query: {
				...router.query,
				action: "update",
				userId: id,
			},
		});
	};

	const goToDeleteUser = (id: string) => {
		return router.push({
			pathname: paths.settings,
			query: {
				...router.query,
				action: "delete",
				userId: id,
			},
		});
	};

	const goToAgentDisplaySettings = () => {
		return router.push({
			query: {
				...router.query,
				action: "settings",
			},
		});
	};

	const goToUpdateAgentQuery = (query: string) => {
		return router.push({
			pathname: paths.agents.list,
			query: {
				...router.query,
				agentQuery: query,
			},
		});
	};

	const goToUpdateClientQuery = (query: string) => {
		return router.push({
			pathname: paths.clients.list,
			query: {
				...router.query,
				clientQuery: query,
			},
		});
	};

	const goToCreateProductCategoryQuery = () => {
		return router.push({
			query: {
				...router.query,
				action: "create",
				dataType: "product_category",
			},
		});
	};

	const goToCreateProductOptionQuery = () => {
		return router.push({
			query: {
				...router.query,
				action: "create",
				dataType: "product_option",
			},
		});
	};

	const goToUpdateProductCategoryQuery = (id: string) => {
		return router.push({
			query: {
				...router.query,
				action: "update",
				dataType: "product_category",
				productCategoryId: id,
			},
		});
	};

	const goToUpdateProductOptionQuery = (id: string) => {
		return router.push({
			query: {
				...router.query,
				action: "update",
				dataType: "product_option",
				productOptionId: id,
			},
		});
	};

	const goToSettingsTab = (newTab: NonNullable<AppQuery["settingsTab"]>) => {
		return router.push({
			query: {
				...router.query,
				settingsTab: newTab,
			},
		});
	};

	const goToReportsTab = (newTab: NonNullable<AppQuery["reportsTab"]>) => {
		return router.push({
			query: {
				...router.query,
				reportsTab: newTab,
			},
		});
	};

	const getReportSummary = (from: string, to: string) => {
		return router.push({
			query: {
				...router.query,
				from: from,
				to: to,
			},
		});
	};

	const getReportOrder = (from: string, to: string) => {
		return router.push({
			query: {
				...router.query,
				from: from,
				to: to,
			},
		});
	};

	const getAgentDetailsQuery = (id: string) => {
		return router.push({
			query: {
				...router.query,
				agentId: id,
			},
		});
	};

	const getClientDetailsQuery = (id: string) => {
		return router.push({
			query: {
				...router.query,
				clientId: id,
			},
		});
	};

	const getProductDetailsQuery = (id: string): Url => {
		return {
			query: {
				...router.query,
				productId: id,
			},
		};
	};

	const goToCreateProductOptionGroupQuery = () => {
		return router.push({
			query: {
				...router.query,
				action: "create",
				dataType: "product_option_group",
			},
		});
	};

	const goToUpdateProductOptionGroupQuery = (id: string) => {
		return router.push({
			query: {
				...router.query,
				action: "update",
				dataType: "product_option_group",
				productOptionGroupId: id,
			},
		});
	};

	const goToFileDetailsQuery = (id: string) => {
		return router.push({
			query: {
				...router.query,
				fileId: id,
			},
		});
	};

	const goToFileDetailsOrderSheetQuery = (id: string) => {
		return router.push({
			query: {
				fileId: id,
				openOrderSheet: true,
			},
		});
	};

	const goToCreateFileQuery = (processId?: string) => {
		return router.push({
			query: {
				...router.query,
				action: "create",
				dataType: "file",
				processId,
			},
		});
	};

	const goToCreateNewCounter = () => {
		return router.push({
			pathname: paths.settings,
			query: {
				...router.query,
				action: "create",
			},
		});
	};

	const goToUpdateCounter = (id: string) => {
		return router.push({
			pathname: paths.settings,
			query: {
				...router.query,
				action: "update",
				counterId: id,
			},
		});
	};

	const goToCreateNewFixDiscount = () => {
		return router.push({
			pathname: paths.fixDiscounts,
			query: {
				...router.query,
				action: "create",
			},
		});
	};

	const goToUpdateFixDiscount = (id: string) => {
		return router.push({
			pathname: paths.fixDiscounts,
			query: {
				...router.query,
				action: "update",
				fixDiscountId: id,
			},
		});
	};

	const goToCreateNewAdditionalFee = () => {
		return router.push({
			pathname: paths.additionalFees,
			query: {
				...router.query,
				action: "create",
			},
		});
	};

	const goToUpdateAdditionalFee = (id: string) => {
		return router.push({
			pathname: paths.additionalFees,
			query: {
				...router.query,
				action: "update",
				additionalFeeId: id,
			},
		});
	};

	const goToAccountsTab = (newTab: NonNullable<AppQuery["accountsTab"]>) => {
		return router.push({
			query: {
				...router.query,
				accountsTab: newTab,
			},
		});
	};

	const goToCreateNewAccount = () => {
		return router.push({
			query: {
				...router.query,
				action: "create",
			},
		});
	};

	const goToUpdateAccount = (id: string) => {
		return router.push({
			query: {
				...router.query,
				action: "update",
				account: id,
			},
		});
	};

	const goToFinancesTab = (newTab: NonNullable<AppQuery["financesTab"]>) => {
		return router.push({
			query: {
				...router.query,
				financesTab: newTab,
			},
		});
	};

	const goToMonthlyStatementPdf = (id: string) => {
		return router.push({
			query: {
				...router.query,
				action: "monthly-statement",
				financeReportId: id,
			},
		});
	};

	return {
		goToAgentDisplaySettings,
		goToCreateFileQuery,
		goToUpdateProductOptionGroupQuery,
		goToCreateProductOptionGroupQuery,
		getProductDetailsQuery,
		getClientDetailsQuery,
		getAgentDetailsQuery,
		goToCreateNewUser,
		goToUpdateUser,
		goToDeleteUser,
		goToUpdateAgentQuery,
		goToUpdateClientQuery,
		goToCreateProductCategoryQuery,
		goToCreateProductOptionQuery,
		goToUpdateProductCategoryQuery,
		goToUpdateProductOptionQuery,
		goToSettingsTab,
		goToReportsTab,
		getReportOrder,
		getReportSummary,
		goToFileDetailsQuery,
		goToFileDetailsOrderSheetQuery,
		goToCreateNewCounter,
		goToUpdateCounter,
		goToCreateNewFixDiscount,
		goToUpdateFixDiscount,
		goToCreateNewAdditionalFee,
		goToUpdateAdditionalFee,
		goToFinancesTab,
		goToMonthlyStatementPdf,
		goToAccountsTab,
		goToCreateNewAccount,
		goToUpdateAccount,
		appQuery,
		router,
		currentActions: {
			isViewingFile: !!appQuery.fileId,
			isCreatingFile:
				appQuery.action === "create" && appQuery.dataType === "file",
		},
	};
};
